import { LeftOutlined } from '@ant-design/icons'
import { Button, Card, Flex, Form, Segmented } from 'antd'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useInvestigationsServiceCreateInvestigationDemandEndpoint } from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import {
  FormFooter,
  message,
  PageBreadcrumbs,
  pickReferralDoctor
} from 'src/components'
import { useUser } from 'src/hooks'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler } from 'src/utils'

import {
  AutomaticallyAddInvestigation,
  ManuallyAddInvestigation
} from '../components'

import { useStyles } from './styles'

const AddTestEnum = {
  MANUALLY: 'manually',
  AUTOMATICALLY: 'automatically'
}

export const AddTestPage = () => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const navigate = useNavigate()
  const { userData } = useUser()

  const [form] = Form.useForm()

  const [type, setType] = useState(AddTestEnum.AUTOMATICALLY)

  const { mutate, isPending } =
    useInvestigationsServiceCreateInvestigationDemandEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors),
      onSuccess: () => {
        message.success(t('consultations.investigation_created_successfully'))
        navigate(paths.tests.root)
        form.resetFields()
      }
    })

  const breadcrumbItems = [
    {
      title: t('menu.tests')
    },
    {
      title: t('tests.add_test')
    }
  ]

  const segmentedItems = [
    {
      value: AddTestEnum.AUTOMATICALLY,
      label: t('tests.add_test_automatically')
    },
    {
      value: AddTestEnum.MANUALLY,
      label: t('tests.add_test_manually')
    }
  ]

  const onBackClick = () => navigate(paths.tests.root)

  const onSave = async () => {
    await form.validateFields()
    const values = form.getFieldsValue(true)

    const payload = {
      doctorId: values.doctorId,
      institutionId: String(userData?.currentLegalEntityId),
      personId: values.personId,
      testTypeId: values.testTypeId,
      harvestDate: dayjs().format(),
      specimenTypes: [values.specimenType],
      ...pickReferralDoctor(values)
    }

    if (!values.personId) {
      message.error(t('investigations.person_error'))
      return
    }

    mutate({ requestBody: payload })
  }

  return (
    <PageBreadcrumbs title={t('tests.add_test')} items={breadcrumbItems}>
      <Flex vertical gap={24}>
        <Button
          icon={<LeftOutlined />}
          className="fit-content"
          onClick={onBackClick}
        >
          {t('common.back')}
        </Button>

        <Card>
          <Form form={form} layout="vertical" requiredMark={false}>
            <Flex vertical gap={24}>
              <Segmented
                className={styles.segmented}
                value={type}
                options={segmentedItems}
                onChange={(val) => {
                  setType(val)
                  form.resetFields()
                }}
              />

              {type === AddTestEnum.AUTOMATICALLY ? (
                <AutomaticallyAddInvestigation form={form} />
              ) : (
                <ManuallyAddInvestigation form={form} />
              )}
            </Flex>

            <FormFooter
              onCancel={onBackClick}
              onSave={onSave}
              saveConfirmTitle={`${t('persons.save')}?`}
              saveLabel={t('persons.save')}
              loading={isPending}
            />
          </Form>
        </Card>
      </Flex>
    </PageBreadcrumbs>
  )
}
